import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import Modal from "./Modal";
import { Mail, MessageCircle } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  const [open, setOpen] = useState(null); // 'terms' | 'privacy' | null

  return (
    <>
      <footer data-testid="site-footer" className="bg-eduhu-dark text-eduhu-bg grain mt-32">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              {/* Logo mark */}
              <Link to="/" aria-label="EDUHUB home" className="inline-block">
                <img
                  src="/logo.png"
                  alt="EDUHUB logo"
                  className="w-20 h-20 sm:w-24 sm:h-24 object-contain"
                />
              </Link>
              {/* Wordmark */}
              <div className="mt-5 font-serif text-5xl md:text-6xl lg:text-7xl leading-none text-eduhu-gold tracking-tight">
                EDUHUB
              </div>
              <div className="mt-4 text-sm tracking-wide text-eduhu-bg/70 max-w-sm">
                {t("footer.tagline")}
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-eduhu-gold mb-4">{t("footer.discipl")}</div>
              <ul className="space-y-2 text-sm text-eduhu-bg/80">
                <li><Link to="/courses?category=programming" className="nav-link">Programming</Link></li>
                <li><Link to="/courses?category=devops" className="nav-link">DevOps</Link></li>
                <li><Link to="/courses?category=qa" className="nav-link">QA</Link></li>
                <li><Link to="/courses?category=robotics" className="nav-link">Robotics</Link></li>
                <li><Link to="/courses?category=languages" className="nav-link">Languages</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-eduhu-gold mb-4">{t("footer.company")}</div>
              <ul className="space-y-2 text-sm text-eduhu-bg/80">
                <li><Link to="/about" className="nav-link">About</Link></li>
                <li><Link to="/courses" className="nav-link">All courses</Link></li>
                <li><Link to="/playground" className="nav-link">Playground</Link></li>
                <li><Link to="/register" className="nav-link">Become a student</Link></li>
                <li><Link to="/teach" className="nav-link">Teach with us</Link></li>
                <li><Link to="/corporate" className="nav-link">Corporate Training</Link></li>
                <li><Link to="/career" className="nav-link">Career Services</Link></li>
                {process.env.REACT_APP_WHATSAPP_NUMBER && (
                  <li>
                    <a
                      href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="nav-link flex items-center gap-1.5"
                    >
                      <MessageCircle size={13} className="text-eduhu-gold" aria-hidden="true" /> WhatsApp
                    </a>
                  </li>
                )}
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-eduhu-gold mb-4">
                <Link to="/kids-academy" className="nav-link">Kids Academy</Link>
              </div>
              <ul className="space-y-2 text-sm text-eduhu-bg/80">
                <li><Link to="/kids-academy?category=kids-coding" className="nav-link">Coding for Kids</Link></li>
                <li><Link to="/kids-academy?category=kids-robotics" className="nav-link">Robotics for Kids</Link></li>
                <li><Link to="/kids-academy?category=kids-mathematics" className="nav-link">Mathematics for Kids</Link></li>
                <li><Link to="/kids-academy?category=kids-english" className="nav-link">English for Kids</Link></li>
                <li><Link to="/kids-academy?category=kids-science" className="nav-link">Science for Kids</Link></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="overline text-eduhu-gold mb-4">{t("footer.legal")}</div>
              <ul className="space-y-2 text-sm text-eduhu-bg/80">
                <li>
                  <button
                    data-testid="footer-terms"
                    onClick={() => setOpen("terms")}
                    className="nav-link text-left"
                  >
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button
                    data-testid="footer-privacy"
                    onClick={() => setOpen("privacy")}
                    className="nav-link text-left"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <Link
                    to="/verify-certificate"
                    data-testid="footer-verify"
                    className="nav-link"
                  >
                    Verify Certificate
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact strip */}
          <div className="mt-14 pt-8 border-t border-white/10 flex flex-wrap items-center gap-6">
            <a href="mailto:hello@eduhubcenter.com"
              className="flex items-center gap-2 text-sm text-eduhu-bg/70 hover:text-eduhu-gold transition-colors">
              <Mail size={14} className="text-eduhu-gold" />
              hello@eduhubcenter.com
            </a>
            <span className="text-eduhu-bg/30 hidden sm:inline">·</span>
            <span className="text-sm text-eduhu-bg/50">Mon–Fri 09:00–18:00 EET</span>
            <span className="text-eduhu-bg/30 hidden sm:inline">·</span>
            <span className="text-sm text-eduhu-bg/50">Bucharest, Romania · Worldwide delivery</span>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between text-xs text-eduhu-bg/50 gap-4">
            <div>{t("footer.copy")}</div>
            <div>Made with conviction · Bucharest · Worldwide</div>
            <div className="font-mono text-eduhu-bg/30">v1.0.0</div>
          </div>
        </div>
      </footer>

      <Modal
        open={open === "terms"}
        onClose={() => setOpen(null)}
        eyebrow="Legal"
        title="Terms of Service"
      >
        <div className="space-y-4">
          <p>Effective date: May 24, 2026. By using EDUHUB  ("EDUHUB", "we", "us"), you agree to these terms.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">1. Accounts</h4>
          <p>You must provide accurate information at registration and keep your password secure. You are responsible for all activity on your account.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">2. Enrollment & Payments</h4>
          <p>Course prices are listed at the time of enrollment. Payments are processed securely. If you have concerns about your enrollment, please contact our support team at hello@eduhubcenter.com.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">3. Cohorts & Minimum Quota</h4>
          <p>Every course begins only when the minimum cohort size is reached. If a cohort does not fill within 30 days of your enrollment, please contact support to discuss your options.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">4. Conduct</h4>
          <p>Harassment, plagiarism, or disruption of live classes leads to immediate dismissal without refund.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">5. Intellectual Property</h4>
          <p>All curricula, slides, recordings and certificates are property of EDUHUB / MikkSysTech. Sharing recordings publicly is prohibited.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">6. Liability</h4>
          <p>EDUHUB provides educational services as-is. We do not guarantee employment outcomes.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">7. Changes</h4>
          <p>We may update these Terms; continued use of the platform constitutes acceptance.</p>
          <p className="text-xs text-eduhu-inkSoft mt-8">
            Questions? Email <span className="font-mono">legal@mikksystech.com</span>.
          </p>
        </div>
      </Modal>

      <Modal
        open={open === "privacy"}
        onClose={() => setOpen(null)}
        eyebrow="Legal"
        title="Privacy Policy"
      >
        <div className="space-y-4">
          <p>Effective date: May 24, 2026. Your privacy is a first-class concern.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">Data we collect</h4>
          <ul className="list-disc pl-5 space-y-1">
            <li>Account: name, email, hashed password (bcrypt — we never see your plaintext password)</li>
            <li>Enrollment & payment: amount, currency, course; card data is held by Stripe, never by us</li>
            <li>Usage: pages visited, courses opened, time spent in live sessions</li>
          </ul>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">How we use it</h4>
          <p>To deliver classes, issue certificates, send transactional emails, and improve the platform.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">Sharing</h4>
          <p>We share data only with: Stripe (payments), Daily.co/Jitsi (live video when enabled), and email-delivery providers. We do not sell your data.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">Your rights (GDPR / CCPA)</h4>
          <p>You may request access, correction, export or deletion of your data at any time by writing to <span className="font-mono">privacy@mikksystech.com</span>.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">Cookies</h4>
          <p>We use httpOnly session cookies for authentication. No third-party advertising cookies.</p>
          <h4 className="font-serif text-lg text-eduhu-dark mt-6">Children &amp; Kids Academy</h4>
          <p>
            Our general courses are intended for users 16 and over. Kids Academy courses (ages
            4–14) are the one exception: a parent or legal guardian must create and control the
            account, and must explicitly confirm their relationship to the child and consent to
            enrollment before we allow it — captured with a timestamp at the point of enrollment.
            We do not knowingly collect personal information directly from a child, and children
            never independently register an account or provide their own contact details.
          </p>
        </div>
      </Modal>

    </>
  );
}
